import axios from 'axios';

// Cache for attachment URLs to avoid unnecessary API calls
const attachmentUrlCache = new Map();
const CACHE_DURATION = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds (much less than 10 year SAS token)

/**
 * Get a fresh attachment URL with a valid SAS token
 * @param {string} identifier - The filename or blob_name stored in the database
 * @returns {Promise<string|null>} - The attachment URL or null if failed
 */
export const getAttachmentUrl = async (identifier) => {
  if (!identifier) return null;
  
  // Check cache first
  const cached = attachmentUrlCache.get(identifier);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.url;
  }
  
  try {
    // Get fresh URL from backend using identifier
    const response = await axios.get(`/api/tasks/attachment/${identifier}`);
    const attachmentUrl = response.data.attachment_url;
    
    if (attachmentUrl) {
      // Cache the new URL
      attachmentUrlCache.set(identifier, {
        url: attachmentUrl,
        timestamp: Date.now()
      });
      return attachmentUrl;
    }
  } catch (error) {
    console.error('Failed to get attachment URL:', error);
    // Remove from cache if there's an error
    attachmentUrlCache.delete(identifier);
  }
  
  return null;
};

/**
 * Remove an attachment from the cache
 * @param {string} identifier - The filename or blob_name to remove
 */
export const removeAttachmentFromCache = (identifier) => {
  if (identifier) {
    attachmentUrlCache.delete(identifier);
  }
};

/**
 * Clear all attachment cache
 */
export const clearAttachmentCache = () => {
  attachmentUrlCache.clear();
};

/**
 * Get cached URL if available (for immediate display)
 * @param {string} identifier - The filename or blob_name
 * @returns {string|null} - Cached URL or null
 */
export const getCachedAttachmentUrl = (identifier) => {
  if (!identifier) return null;
  
  const cached = attachmentUrlCache.get(identifier);
  if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
    return cached.url;
  }
  
  return null;
};
